import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Space,
  Radio,
  Button,
  Drawer,
  Select,
  DatePicker,
  InputNumber,
  useSnackbar,
} from 'design-system';

import { User } from 'src/services/user';
import { useAuthContext } from 'src/auth/hooks';
import { createActionPlan } from 'src/services/actionPlan';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface ActionPlanCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  users: User[];
  departments: { label: string; value: string }[];
  onSuccess?: () => void;
}

interface FormValues {
  department: string;
  coordinators: number[];
  description: string;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  assessmentDimension: 'Duration' | 'Quantity' | 'Boolean';
  // 按数量维度的字段
  planQuantity?: number;
  actualQuantity?: number;
  unit?: string;
}

export default function ActionPlanCreateDrawer({
  open,
  onClose,
  users,
  departments,
  onSuccess,
}: ActionPlanCreateDrawerProps) {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  // 考核判断维度选项
  const assessmentDimensionOptions = [
    { label: '按时间', value: 'Duration' },
    { label: '按数量', value: 'Quantity' },
    { label: '是与否', value: 'Boolean' },
  ];

  // 单位选项
  const unitOptions = [
    { label: '元', value: '元' },
    { label: '个/次', value: '个/次' },
  ];

  // 责任人选项
  const coordinatorOptions =
    users
      ?.map((user) => ({
        label: `${user.lastName}${user.firstName}`,
        value: user.id,
      }))
      .filter((option) => option.value !== undefined) || [];

  // 监听考核判断维度变化
  const assessmentDimension = Form.useWatch('assessmentDimension', form);

  // 自动填充考核部门
  useEffect(() => {
    if (open && user?.category?.id && departments.length > 0) {
      const currentFormValues = form.getFieldsValue();
      if (!currentFormValues.department) {
        form.setFieldValue('department', user.category.id.toString());
      }
    }
  }, [open, user, departments, form]);

  // 当考核判断维度变化时，清空相关字段
  useEffect(() => {
    const fieldsToReset: Partial<FormValues> = {};

    if (assessmentDimension !== 'Quantity') {
      fieldsToReset.planQuantity = undefined;
      fieldsToReset.actualQuantity = undefined;
      fieldsToReset.unit = undefined;
    }

    form.setFieldsValue(fieldsToReset);
  }, [assessmentDimension, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // 查找部门信息
      const department = departments.find((dept) => dept.value === values.department);

      // 查找责任人信息
      const coordinators = users.filter(
        (user) => user.id !== undefined && values.coordinators.includes(user.id)
      );

      // 构造metadata
      let metadata = {};

      if (values.assessmentDimension === 'Quantity') {
        metadata = {
          planQuantity: values.planQuantity,
          unit: values.unit,
          // 只有当用户实际输入了actualQuantity时才包含在metadata中
          ...(values.actualQuantity !== undefined &&
            values.actualQuantity !== null && {
              actualQuantity: values.actualQuantity,
            }),
        };
      }

      // 构造创建 payload - 移除status字段，因为创建时不应该包含status
      const createPayload = {
        description: values.description,
        planStartDate: values.dateRange[0].format('YYYY-MM-DD'),
        planEndDate: values.dateRange[1].format('YYYY-MM-DD'),
        department: department
          ? {
              id: department.value,
              name: department.label,
              parentId: null,
            }
          : undefined,
        coordinators,
        source: 'ManualCreated',
        assessmentDimension: values.assessmentDimension,
        metadata,
      };

      // 调用API创建行动计划
      await createActionPlan(createPayload);

      enqueueSnackbar('行动计划创建成功', { variant: 'success' });

      // 重置表单
      form.resetFields();

      // 关闭抽屉
      onClose();

      // 调用成功回调
      onSuccess?.();
    } catch (error) {
      // 错误处理
      enqueueSnackbar('创建行动计划失败', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title="新建行动计划"
      width={590}
      open={open}
      onClose={handleCancel}
      bodyStyle={{ padding: '16px', paddingLeft: '24px' }}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>取消</Button>
            <Button type="primary" onClick={handleSave} loading={loading}>
              保存
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ flex: '110px' }}
        wrapperCol={{ flex: 'auto' }}
        onFinish={handleSave}
        colon={false}
      >
        <Form.Item
          name="department"
          label="考核部门"
          rules={[{ required: true, message: '请选择部门' }]}
        >
          <Select
            placeholder="请选择部门"
            options={departments}
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="coordinators"
          label="责任人"
          rules={[{ required: true, message: '请选择责任人' }]}
        >
          <Select
            placeholder="请选择责任人"
            options={coordinatorOptions}
            mode="multiple"
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="行动计划描述"
          rules={[{ required: true, message: '请输入行动计划描述' }]}
        >
          <TextArea placeholder="请输入行动计划描述" rows={4} maxLength={500} showCount />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="计划任务周期"
          rules={[{ required: true, message: '请选择计划任务周期' }]}
        >
          <RangePicker style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
        </Form.Item>

        <Form.Item
          name="assessmentDimension"
          label="考核判断维度"
          rules={[{ required: true, message: '请选择考核判断维度' }]}
        >
          <Radio.Group>
            {assessmentDimensionOptions.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {/* 按数量维度的字段 */}
        {assessmentDimension === 'Quantity' && (
          <Form.Item label="目标数量">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="planQuantity"
                noStyle
                rules={[
                  { required: true, message: '请输入目标数量' },
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined) {
                        return Promise.reject(new Error('请输入目标数量'));
                      }
                      if (typeof value === 'number' && value >= 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('目标数量必须大于等于0'));
                    },
                  },
                ]}
              >
                <InputNumber
                  placeholder="请输入目标数量"
                  style={{ flex: 1 }}
                  min={0}
                  precision={2}
                  isCurrency
                  controls={false}
                />
              </Form.Item>
              <Form.Item
                name="unit"
                noStyle
                rules={[{ required: true, message: '请选择单位' }]}
                initialValue="元"
              >
                <Select placeholder="单位" style={{ width: 120 }} options={unitOptions} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
